import React, { useCallback } from 'react';

import Avatar from '@atlaskit/avatar/avatar';
import { cssMap } from '@atlaskit/css';
import { expVal } from '@atlaskit/platform-feature-experiments/exp-val';
import { fg } from '@atlaskit/platform-feature-flags/fg';
import { Box } from '@atlaskit/primitives/compiled/box';
import { AgentCard, type AgentCardCoverProps } from '@atlassian/studio-browse-kit/agent-card';
import { CardCover } from '@atlassian/studio-browse-kit/ui/card-cover';

import {
	ExternalAgentAttribution,
	type ExternalAgentChannel,
	type ExternalAgentInvokedByInput,
	type ExternalAgentInvokingUserRenderer,
} from './ExternalAgentAttribution';
import { ExternalAgentAvatarFrame } from './ExternalAgentAvatarFrame';

export type {
	ExternalAgentChannel,
	ExternalAgentInvokedBy,
	ExternalAgentInvokedByInput,
	ExternalAgentInvokingUserRenderer,
} from './ExternalAgentAttribution';

const styles = cssMap({
	card: { width: '220px' },
});

const ExternalAgentAvatar = ({
	name,
	iconUrl,
}: {
	name: string;
	iconUrl?: string;
}): React.JSX.Element => {
	const isUplift = expVal('jira_browse_agent_modal_update', 'isEnabled', false);
	const avatar = (
		<Avatar
			name={name}
			src={iconUrl}
			appearance="hexagon"
			size={isUplift ? 'xlarge' : 'small'}
			UNSAFE_isUpdatedGeometry={fg('platform-dst-avatar-updated-geometry')}
		/>
	);

	return isUplift ? <ExternalAgentAvatarFrame>{avatar}</ExternalAgentAvatarFrame> : avatar;
};

const ExternalAgentCover = (props: AgentCardCoverProps): React.JSX.Element => (
	<CardCover {...props} avatar={ExternalAgentAvatar} />
);

const NoMetadata = (): null => null;

export type ExternalAgentProfileCardProps = {
	/** Identity account ID of the external AppUser. */
	accountId: string;
	name: string;
	iconUrl?: string;
	/** Secondary line under the name, e.g. the vendor that publishes the agent. */
	vendorLine?: string;
	description?: string;
	/** One invoking person or a short facepile for aggregate usage. */
	withUser?: ExternalAgentInvokedByInput;
	/** Optional host profile-card trigger for invoking people with account IDs. */
	renderInvokingUser?: ExternalAgentInvokingUserRenderer;
	/** Renders the channel attribution when the invoking person is also supplied. */
	via?: ExternalAgentChannel;
	testId?: string;
};

/** Presents an external AppUser in the shared agent card without querying Studio. */
export const ExternalAgentProfileCard = ({
	accountId,
	name,
	iconUrl,
	vendorLine,
	description,
	withUser,
	renderInvokingUser,
	via,
	testId,
}: ExternalAgentProfileCardProps): React.JSX.Element => {
	const hasInvokingUser = Boolean(withUser && ('name' in withUser || withUser.length > 0));
	const ExternalAgentFooter = useCallback(
		() => (
			<ExternalAgentAttribution
				invokedBy={withUser}
				invokedVia={via}
				renderInvokingUser={renderInvokingUser}
				testId={testId ? `${testId}--attribution` : undefined}
			/>
		),
		[withUser, via, renderInvokingUser, testId],
	);
	return (
		<Box xcss={styles.card}>
			{/* MCP AppUsers use Browse Kit's active card layout without querying Studio. */}
			<AgentCard
				agent={{ id: accountId, name, iconUrl, description, creatorName: vendorLine }}
				slots={{
					cover: ExternalAgentCover,
					metadata: NoMetadata,
					footer: hasInvokingUser ? ExternalAgentFooter : null,
				}}
				testId={testId}
			/>
		</Box>
	);
};
